import type { InfoHintDetail } from '@cou/admin-ui';
import { formatDateTime } from '../contests/format-date';
import type { CatalogueEvent, CatalogueMarket } from './catalogue-client';

/**
 * The rows behind the ⓘ hints on the Catalogue browse.
 *
 * This screen is the Live catalogue — our mirror of the feed — so it is the one place where the
 * **external** id matters as much as ours: it is what an operator quotes to Caesars when a fixture
 * or a prop looks wrong upstream. Both ids are copyable for that reason.
 */
export function catalogueEventDetails(
  event: Pick<CatalogueEvent, 'id' | 'externalEventId' | 'startTime' | 'competition' | 'status'>,
): InfoHintDetail[] {
  const rows: InfoHintDetail[] = [
    { label: 'Event ID', value: event.id, copyable: true },
    { label: 'Feed event ID', value: event.externalEventId, copyable: true },
    { label: 'Starts', value: formatDateTime(event.startTime) },
  ];
  // The row's own meta line falls back to "MLB" when the feed gave nothing (formatEventMeta);
  // a dashed row here would argue with it, so say nothing instead.
  if (event.competition !== null) rows.push({ label: 'Competition', value: event.competition });
  rows.push({ label: 'Status', value: event.status });
  return rows;
}

export function catalogueMarketDetails(
  market: Pick<CatalogueMarket, 'id' | 'externalMarketId' | 'marketType' | 'flavour' | 'line'>,
): InfoHintDetail[] {
  return [
    { label: 'Market ID', value: market.id, copyable: true },
    { label: 'Feed market ID', value: market.externalMarketId, copyable: true },
    { label: 'Type', value: market.marketType },
    // Every v1 Line is a half-point; one decimal is the honest precision (CONTEXT → Line).
    { label: 'Line', value: market.line.toFixed(1) },
    // We consume only the over/under flavour — showing it makes a stray milestone prop obvious.
    { label: 'Flavour', value: market.flavour },
  ];
}
